import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../services/contexts/AuthContext';
import { 
  Activity, 
  Users, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Play,
  Pause,
  Settings,
  BarChart3,
  TrendingUp,
  Shield,
  Database,
  Zap,
  Monitor,
  Camera,
  HardDrive,
  Wifi,
  Battery,
  Thermometer,
  Gauge
} from 'lucide-react';

const TenantTechnicianDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    totalPatients: 0,
    activeSessions: 0,
    completedRecordings: 0,
    pendingRecordings: 0,
    equipmentStatus: {
      audioCapture: 'operational',
      videoCapture: 'operational', 
      processingUnit: 'operational',
      networkConnection: 'operational'
    },
    recentSessions: [],
    systemPerformance: {
      cpu: 42,
      memory: 68,
      storage: 85,
      network: 92
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setDashboardData({
        totalPatients: 156,
        activeSessions: 8,
        completedRecordings: 89,
        pendingRecordings: 12,
        equipmentStatus: {
          audioCapture: 'operational',
          videoCapture: 'operational',
          processingUnit: 'operational',
          networkConnection: 'operational'
        },
        recentSessions: [
          { id: 1, alias: 'John Doe', status: 'completed', duration: '2:34', quality: 'high' },
          { id: 2, alias: 'Jane Smith', status: 'in_progress', duration: '1:45', quality: 'medium' },
          { id: 3, alias: 'Mike Johnson', status: 'completed', duration: '3:12', quality: 'high' }
        ],
        systemPerformance: {
          cpu: 42,
          memory: 68,
          storage: 85,
          network: 92
        }
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = "orange", change = null, status = null }) => (
    <div className={`bg-white p-6 rounded-xl shadow-sm border-l-4 border-l-${color}-500 border-r border-t border-b border-va-neutral-200`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-va-neutral-600">{title}</p>
          <p className={`text-2xl font-bold mt-2 text-${color}-600`}>{value}</p>
          {change && (
            <p className={`text-sm mt-1 ${change > 0 ? 'text-va-health-600' : 'text-va-error-600'}`}>
              {change > 0 ? '+' : ''}{change}% from yesterday
            </p>
          )}
          {status && (
            <p className="text-xs text-va-neutral-500 mt-1">{status}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const EquipmentStatus = () => (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-semibold text-va-neutral-900">Equipment Status</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <div className="flex items-center space-x-3 p-3 bg-va-health-50 rounded-lg">
          <div className="p-2 bg-va-health-100 rounded-lg">
            <Monitor className="h-5 w-5 text-va-health-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-va-neutral-900">Audio Capture</p>
            <p className="text-xs text-va-neutral-500">Operational</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-3 bg-va-health-50 rounded-lg">
          <div className="p-2 bg-va-health-100 rounded-lg">
            <Camera className="h-5 w-5 text-va-health-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-va-neutral-900">Video Capture</p>
            <p className="text-xs text-va-neutral-500">Operational</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-3 bg-va-health-50 rounded-lg">
          <div className="p-2 bg-va-health-100 rounded-lg">
            <HardDrive className="h-5 w-5 text-va-health-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-va-neutral-900">Processing Unit</p>
            <p className="text-xs text-va-neutral-500">Operational</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-3 bg-va-health-50 rounded-lg">
          <div className="p-2 bg-va-health-100 rounded-lg">
            <Wifi className="h-5 w-5 text-va-health-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-va-neutral-900">Network</p>
            <p className="text-xs text-va-neutral-500">Connected</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-va-neutral-900">Technical Operations Dashboard</h1>
        <p className="text-va-neutral-600">Monitor equipment status and patient sessions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Patients" 
          value={dashboardData.totalPatients} 
          icon={Users} 
          color="brand-blue"
          change={12}
        />
        <StatCard 
          title="Active Sessions" 
          value={dashboardData.activeSessions} 
          icon={Activity} 
          color="brand-cyan"
          change={5}
        />
        <StatCard 
          title="Completed Recordings" 
          value={dashboardData.completedRecordings} 
          icon={CheckCircle} 
          color="va-health-600"
          change={8}
        />
        <StatCard 
          title="Pending Recordings" 
          value={dashboardData.pendingRecordings} 
          icon={Clock} 
          color="va-warning-600"
          change={-3}
        />
      </div>

      {/* Equipment Status */}
      <EquipmentStatus />

      {/* Recent Sessions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-va-neutral-900">Recent Patient Sessions</h3>
        <div className="mt-4 space-y-4">
          {dashboardData.recentSessions.map((session) => (
            <div key={session.id} className="flex items-center justify-between p-4 border border-va-neutral-200 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-blue to-brand-darkCyan rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {session.alias.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-va-neutral-900">{session.alias}</p>
                  <p className="text-sm text-va-neutral-500">
                    {session.duration} • {session.quality} quality
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  session.status === 'completed' 
                    ? 'bg-va-health-100 text-va-health-800' 
                    : 'bg-va-warning-100 text-va-warning-800'
                }`}>
                  {session.status === 'completed' ? 'Completed' : 'In Progress'}
                </span>
                <button className="p-2 text-va-neutral-400 hover:text-brand-blue">
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Technical Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-va-neutral-900">Technical Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <button className="p-4 border border-va-neutral-200 rounded-lg hover:border-brand-blue hover:bg-va-primary-50 transition-colors">
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-brand-blue" />
              <span className="font-medium text-va-neutral-900">Add New Patient</span>
            </div>
          </button>
          <button className="p-4 border border-va-neutral-200 rounded-lg hover:border-brand-cyan hover:bg-va-accent-50 transition-colors">
            <div className="flex items-center space-x-3">
              <Activity className="h-5 w-5 text-brand-cyan" />
              <span className="font-medium text-va-neutral-900">Send Recording Links</span>
            </div>
          </button>
          <button className="p-4 border border-va-neutral-200 rounded-lg hover:border-va-health-600 hover:bg-va-health-50 transition-colors">
            <div className="flex items-center space-x-3">
              <Settings className="h-5 w-5 text-va-health-600" />
              <span className="font-medium text-va-neutral-900">Equipment Check</span>
            </div>
          </button>
        </div>
      </div>

      {/* System Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-va-neutral-900">Collection Insights</h3>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-va-neutral-600">Success Rate</span>
              <span className="text-sm font-medium text-va-neutral-900">94%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-va-neutral-600">Average Duration</span>
              <span className="text-sm font-medium text-va-neutral-900">2.3 min</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-va-neutral-600">Quality Score</span>
              <span className="text-sm font-medium text-va-neutral-900">8.7/10</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-va-neutral-900">System Performance</h3>
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-va-neutral-600">CPU Usage</span>
              <span className="text-sm font-medium text-va-neutral-900">42%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-va-neutral-600">Memory Usage</span>
              <span className="text-sm font-medium text-va-neutral-900">68%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-va-neutral-600">Storage</span>
              <span className="text-sm font-medium text-va-neutral-900">85%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-va-neutral-600">Network</span>
              <span className="text-sm font-medium text-va-neutral-900">92%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantTechnicianDashboard;